import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiClose } from '@mdi/js'
import { AssetTypePicker } from '../asset-type-picker/AssetTypePicker'
import { useAssetTypes } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'

interface AssetTypeMeta {
    uid: string
    code: string
    /**
     * The API returns a multilingual object here, not a string. It was typed
     * `string` for a long time and got away with it only because the label was
     * built through a template literal, which stringified the object into
     * `[object Object]` rather than crashing. Rendering it directly is React
     * error #31.
     */
    name?: string | Record<string, string>
    template_names?: Record<string, string>
}

export interface AssetTypeCodesFieldProps {
    org: string
    label: string
    /** Selected asset-type **codes** (the API stores codes, not uids). */
    value: string[]
    onChange: (codes: string[]) => void
    disabled?: boolean
    testIdPrefix: string
    /** Shown when nothing is selected; defaults to the relation types' "any type". */
    emptyLabel?: string
    /** Help under the picker; defaults to the ancestor-matching hint. */
    hint?: string
}

/**
 * Multi-select over the org's asset types, storing **codes** because
 * `possible_source_asset_type_codes` / `possible_destination_asset_type_codes`
 * are matched by the backend against the asset type's own code *or any of its
 * ancestor codes* — so picking `trade_hvac` covers every HVAC equipment type.
 *
 * The picker itself is the shared hierarchical `AssetTypePicker` (§13 of
 * docs/frontend/patterns.md) driven in "add one" mode: it always renders with
 * an empty selection and every pick appends a chip.
 */
export function AssetTypeCodesField({
    org,
    label,
    value,
    onChange,
    disabled,
    testIdPrefix,
    emptyLabel,
    hint,
}: AssetTypeCodesFieldProps) {
    const { t } = useTranslation('types')
    const { locale } = useLocale()
    const { data: assetTypesRaw = [] } = useAssetTypes(org)
    const assetTypes = assetTypesRaw as unknown as AssetTypeMeta[]

    const byUid = useMemo(() => {
        const m = new Map<string, AssetTypeMeta>()
        for (const at of assetTypes) m.set(at.uid, at)
        return m
    }, [assetTypes])

    const byCode = useMemo(() => {
        const m = new Map<string, AssetTypeMeta>()
        for (const at of assetTypes) m.set(at.code, at)
        return m
    }, [assetTypes])

    const labelForCode = useCallback(
        (code: string) => {
            const at = byCode.get(code)
            if (!at) return code
            return (
                getLocalizedName(at.template_names, locale) ||
                (typeof at.name === 'string' ? at.name : getLocalizedName(at.name, locale)) ||
                code
            )
        },
        [byCode, locale]
    )

    const handlePick = useCallback(
        (uid: string) => {
            const at = byUid.get(uid)
            if (!at || !at.code) return
            if (value.includes(at.code)) return
            onChange([...value, at.code])
        },
        [byUid, onChange, value]
    )

    const handleRemove = useCallback(
        (code: string) => onChange(value.filter(c => c !== code)),
        [onChange, value]
    )

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
            <div className="text-sm font-medium text-foreground">{label}</div>
            <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-chips`}>
                {value.length === 0 ? (
                    <span className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
                        {emptyLabel ?? t('relation_types.form.types_unrestricted')}
                    </span>
                ) : (
                    value.map(code => (
                        <span
                            key={code}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
                            data-testid={`${testIdPrefix}-chip-${code}`}
                        >
                            <span className="font-medium">{labelForCode(code)}</span>
                            <code className="text-[10px] text-muted-foreground">{code}</code>
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(code)}
                                    aria-label={t('relation_types.form.types_remove', { code })}
                                    title={t('relation_types.form.types_remove', { code })}
                                    data-testid={`${testIdPrefix}-remove-${code}`}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <AppIcon path={mdiClose} size="xs" />
                                </button>
                            )}
                        </span>
                    ))
                )}
            </div>
            {!disabled && (
                <AssetTypePicker
                    org={org}
                    value=""
                    onChange={handlePick}
                    placeholder={t('relation_types.form.types_add')}
                    ariaLabel={t('relation_types.form.types_add')}
                    triggerTestId={`${testIdPrefix}-picker`}
                    className="max-w-md"
                />
            )}
            <p className="text-xs text-muted-foreground">
                {hint ?? t('relation_types.form.types_ancestor_hint')}
            </p>
        </div>
    )
}
